import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REGIONS = ["India", "Asia", "Europe", "North America", "Africa", "Oceania"];

const inputCls =
  "w-full bg-[#FBF9F5]/60 border border-[#0D1117]/15 rounded-xl px-4 py-3 text-sm text-[#0D1117] placeholder:text-[#6B7280] focus:border-[#FF4E00]/60 focus:outline-none transition-all duration-300";

export default function AdminCalendar({ token }) {
  const [races, setRaces] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ race: "", city: "", month: "", d: "", region: "India", dist: "", reg_url: "" });
  const authed = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/races`);
      setRaces(r.data);
    } catch {
      toast.error("Could not load races");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/races`,
        {
          ...form,
          dist: form.dist.split(",").map((s) => s.trim()).filter(Boolean),
        },
        authed
      );
      toast.success("Race added to the calendar");
      setForm({ race: "", city: "", month: "", d: "", region: "India", dist: "", reg_url: "" });
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not add race");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/races/${id}`, authed);
      toast.success("Race removed");
      load();
    } catch {
      toast.error("Could not delete race");
    }
  };

  const months = [...new Set(races.map((r) => r.month))];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="admin-calendar">
      <form onSubmit={create} className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 space-y-4 h-fit">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00]">Add Race</p>
        <input required placeholder="Race name — e.g. Goa River Marathon" value={form.race} onChange={(e) => setForm({ ...form, race: e.target.value })} className={inputCls} data-testid="admin-race-name" />
        <input required placeholder="City, Country — e.g. Goa, India" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} data-testid="admin-race-city" />
        <div className="grid grid-cols-2 gap-3">
          <input required placeholder="Month — e.g. Dec 2026" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className={inputCls} data-testid="admin-race-month" />
          <input required placeholder="Day — e.g. 13 Dec" value={form.d} onChange={(e) => setForm({ ...form, d: e.target.value })} className={inputCls} data-testid="admin-race-day" />
        </div>
        <select value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className={`${inputCls} appearance-none`} data-testid="admin-race-region">
          {REGIONS.map((r) => (
            <option key={r} value={r} className="bg-[#FFFFFF]">{r}</option>
          ))}
        </select>
        <input placeholder="Distances, comma separated — e.g. Marathon, Half, 10K" value={form.dist} onChange={(e) => setForm({ ...form, dist: e.target.value })} className={inputCls} data-testid="admin-race-dist" />
        <input type="url" placeholder="Registration link — IndiaRunning / iFinish / Ahotu / official event URL" value={form.reg_url} onChange={(e) => setForm({ ...form, reg_url: e.target.value })} className={inputCls} data-testid="admin-race-regurl" />
        <button type="submit" disabled={saving} data-testid="admin-race-create" className="w-full inline-flex items-center justify-center bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-full transition-all duration-300">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add to Calendar"}
        </button>
      </form>

      <div className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 max-h-[70vh] overflow-y-auto">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00] mb-5">
          Calendar Races · {races.length}
        </p>
        {months.map((m) => (
          <div key={m} className="mb-5">
            <p className="font-serif italic text-base text-[#FF4E00] mb-2">{m}</p>
            <ul className="space-y-2">
              {races.filter((r) => r.month === m).map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-4 text-sm border-b border-[#0D1117]/5 pb-2">
                  <span className="text-[#0D1117] truncate">
                    {r.race} <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#6B7280] ml-2">{r.d} · {r.city}</span>
                  </span>
                  <button onClick={() => remove(r.id)} data-testid="admin-race-delete" className="text-[#6B7280] hover:text-red-600 transition-colors shrink-0" aria-label={`Delete ${r.race}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {races.length === 0 && <p className="text-sm text-[#6B7280]">No races yet.</p>}
      </div>
    </div>
  );
}
